import { createPageTracker, initialPageView, type PageView } from './page-view'

type GoogleTagCommand =
  | ['js', Date]
  | ['config', string, Record<string, unknown>]
  | ['set', PageView]
  | ['event', 'page_view', PageView & { send_to: string }]

declare global {
  interface Window {
    gtag?: (...args: GoogleTagCommand) => void
  }
}

/** Loaded once at app startup, outside React StrictMode's repeated effects. */
export function createGoogleAnalytics(measurementId: string, debug = false) {
  if (!measurementId || typeof window === 'undefined' || typeof document === 'undefined') return

  window.dataLayer ??= []
  window.gtag ??= function (..._args: GoogleTagCommand) {
    // Google's queue consumes Arguments objects, not plain event objects/arrays.
    // eslint-disable-next-line prefer-rest-params -- Match Google's gtag queue contract.
    window.dataLayer?.push(arguments)
  }
  window.gtag('js', new Date())
  // Global defaults can be updated on navigation; config-scoped fields would
  // override later `set` commands for automatic events.
  window.gtag('set', initialPageView())
  window.gtag('config', measurementId, {
    send_page_view: false,
    ...(debug ? { debug_mode: true } : {}),
  })

  if (!document.getElementById('ga4-script')) {
    const script = document.createElement('script')
    script.id = 'ga4-script'
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
    document.head.appendChild(script)
  }

  return createPageTracker((page) => {
    // Keep subsequent automatic events on the sanitized, currently resolved page.
    window.gtag?.('set', page)
    window.gtag?.('event', 'page_view', { ...page, send_to: measurementId })
  })
}
